import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Settings, Check, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // Track which plan is loading
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    setIsLoggedIn(!!token);
  }, []);

  const tiers = [
    {
      id: "starter",
      name: "Starter",
      price: "$49",
      priceValue: 49,
      period: "per year",
      certificates: "250 certificates/year",
      features: [
        "All templates (20+)",
        "Basic tracking (views, downloads)",
        "Public verification pages",
        "PDF & PNG export",
        "LinkedIn sharing",
        "Custom logo upload",
        "Email support"
      ]
    },
    {
      id: "professional",
      name: "Professional",
      price: "$99",
      priceValue: 99,
      period: "per year",
      certificates: "1,000 certificates/year",
      popular: true,
      features: [
        "All templates (20+)",
        "Full tracking & analytics",
        "Public verification pages",
        "PDF & PNG export",
        "LinkedIn sharing",
        "Priority support",
        "Custom logo upload"
      ]
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "$199",
      priceValue: 199,
      period: "per year",
      certificates: "Unlimited certificates",
      features: [
        "All templates (20+)",
        "Full tracking & analytics",
        "Public verification pages",
        "PDF & PNG export",
        "LinkedIn sharing",
        "Priority support",
        "Custom branding removal",
        "Custom logo upload",
        "Bulk operations"
      ]
    }
  ];

  const handleSelectPlan = async (planId) => {
    // If not logged in, redirect to signup
    if (!isLoggedIn) {
      toast.info("Please create an account first");
      navigate("/issuer/signup");
      return;
    }

    setLoading(planId);

    try {
      const token = localStorage.getItem('issuer_token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          plan_id: planId,
          origin_url: originUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50" data-testid="pricing-nav">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2" onClick={() => navigate("/")} style={{cursor: 'pointer'}}>
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="nav-home-btn">Home</Button>
            {isLoggedIn ? (
              <Button onClick={() => navigate("/issuer/dashboard")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2" data-testid="nav-dashboard-btn">Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/issuer/login")} data-testid="nav-login-btn">Login</Button>
                <Button onClick={() => navigate("/issuer/signup")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2" data-testid="nav-signup-btn">Get Started</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="py-20" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-medium tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Playfair Display'}}>Simple, Transparent Pricing</h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto">Annual plans with straightforward pricing. Choose the plan that fits your needs.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier, idx) => (
              <div 
                key={idx}
                className={`p-6 bg-white border rounded-xl smooth-transition hover-lift ${
                  tier.popular ? 'border-blue-600 shadow-lg' : 'border-slate-200 shadow-sm'
                }`}
                data-testid={`pricing-tier-${tier.id}`}
              >
                {tier.popular && (
                  <div className="bg-blue-600 text-white text-sm font-medium px-3 py-1 rounded-full inline-block mb-4" data-testid="popular-badge">Most Popular</div>
                )}
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>{tier.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                  <span className="text-slate-600 ml-2">{tier.period}</span>
                </div>
                <p className="text-sm font-medium text-amber-600 mb-6">{tier.certificates}</p>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`feature-${tier.id}-${i}`}>
                      <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => handleSelectPlan(tier.id)}
                  disabled={loading !== null}
                  className={`w-full rounded-lg py-3 font-medium ${
                    tier.popular 
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`select-plan-${tier.id}-btn`}
                >
                  {loading === tier.id ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    isLoggedIn ? "Subscribe Now" : "Get Started"
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* AppSumo Banner */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
                  <Gift className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Have an AppSumo code?</h3>
                  <p className="text-sm text-slate-600">Redeem your lifetime deal and unlock instant access</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/appsumo")}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100 rounded-lg px-6"
                data-testid="redeem-appsumo-btn"
              >
                Redeem Code
              </Button>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-slate-500">All plans renew annually. Cancel anytime. No hidden fees.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">© 2025 Certificate Machine. Professional credentials made simple.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
